import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiGet } from '../../utils/api';
import { Button, Card, Spinner, Badge } from '../design-system';
import { 
  Building2, Users, Settings, Info, AlertCircle, UserPlus, Mail, 
  BarChart3, Calendar, Activity, Phone, MapPin
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import PracticeSelection from './PracticeSelection';
import PracticeMembersList from './PracticeMembersList';
import JoinRequestsList from './JoinRequestsList';
import InviteMemberModal from './InviteMemberModal';
import PracticeAnalytics from './PracticeAnalytics';
import PracticeInfoModal from './PracticeInfoModal';

const PracticeSettings = () => {
  const { userInfo, refreshUserInfo } = useAuth();
  const [practice, setPractice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);

  useEffect(() => {
    fetchPractice();
    checkPendingRequest();
  }, []);

  const fetchPractice = async () => {
    try {
      setLoading(true);
      const response = await apiGet('/.netlify/functions/get-practice');
      if (response.practice) {
        setPractice(response);
        setPendingRequest(null);
      }
    } catch (error) {
      console.error('Error fetching practice:', error);
      if (error.message && !error.message.includes('does not belong')) {
        // Don't show error if we're checking for pending requests
      }
    } finally {
      setLoading(false);
    }
  };

  const checkPendingRequest = async () => {
    try {
      const response = await apiGet('/.netlify/functions/get-user-join-request');
      if (response.hasPendingRequest && response.request) {
        setPendingRequest(response.request);
      }
    } catch (error) {
      console.error('Error checking pending request:', error);
    }
  };

  const handlePracticeCreated = async () => {
    await refreshUserInfo();
    await fetchPractice();
    await checkPendingRequest();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="large" />
      </div>
    );
  }

  // If user doesn't have a practice, check for pending request
  if (!practice || !practice.practice) {
    if (pendingRequest) {
      return (
        <div className="space-y-4 md:space-y-6">
          <Card variant="elevated" padding="large">
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="h-10 w-10 text-orange-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">
                Join Request Pending
              </h2>
              <p className="text-gray-600 mb-2 text-lg">
                Your request to join <strong className="text-gray-900">{pendingRequest.practiceName}</strong> is pending approval.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Requested on {new Date(pendingRequest.requestedAt).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                <p className="text-sm text-blue-800">
                  The practice admin will review your request and notify you once it's been processed.
                </p>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return (
      <div className="space-y-4 md:space-y-6">
        <Card variant="elevated" padding="large">
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Building2 className="h-10 w-10 text-gray-400" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              No Practice Found
            </h2>
            <p className="text-gray-600 mb-8 text-lg max-w-md mx-auto">
              You're not currently part of a practice. Create a new practice or join an existing one to get started.
            </p>
            <PracticeSelection onPracticeSelected={handlePracticeCreated} />
          </div>
        </Card>
      </div>
    );
  }

  const practiceData = practice?.practice;
  const memberData = practice?.member;

  // Safety check - should not happen due to check above, but defensive programming
  if (!practiceData || !memberData) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="large" />
      </div>
    );
  }

  // Tab configuration - removed analytics tab, it's now in overview
  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    ...(memberData?.isAdmin ? [
      { id: 'members', label: 'Members', icon: Users },
      { id: 'requests', label: 'Join Requests', icon: UserPlus },
    ] : [])
  ];

  return (
    <div className="space-y-6">

      {/* Tabs for Admin Features */}
      {memberData?.isAdmin && (
        <Card variant="elevated" padding="none">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <nav className="flex overflow-x-auto flex-1 scrollbar-hide" aria-label="Tabs">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center gap-1.5 md:gap-2 px-3 md:px-6 py-3 md:py-4 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap
                        transition-colors duration-200 flex-shrink-0
                        ${isActive
                          ? 'border-primary text-primary bg-white'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                      `}
                    >
                      <Icon className={`h-3.5 w-3.5 md:h-4 md:w-4 ${isActive ? 'text-primary' : ''}`} />
                      <span className="hidden sm:inline">{tab.label}</span>
                      <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                    </button>
                  );
                })}
              </nav>
              <div className="px-2 md:px-4 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => setShowInfoModal(true)}
                  title="Practice settings and information"
                  className="p-2"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-4 md:p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Analytics Section */}
                <div>
                  <div className="mb-3 md:mb-4">
                    <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1">Practice Analytics</h3>
                    <p className="text-xs md:text-sm text-gray-600">Performance metrics and insights for your practice</p>
                  </div>
                  <PracticeAnalytics 
                    memberCount={practiceData.memberCount || 0}
                    patientCount={practiceData.patientCount || 0}
                  />
                </div>
              </div>
            )}

            {activeTab === 'members' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4 mb-4 md:mb-6">
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-gray-900">Practice Members</h3>
                    <p className="text-xs md:text-sm text-gray-600 mt-1">Manage your practice team</p>
                  </div>
                  <Button
                    variant="primary"
                    size="medium"
                    onClick={() => setShowInviteModal(true)}
                    className="w-full sm:w-auto"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Invite Member
                  </Button>
                </div>
                <PracticeMembersList onMemberUpdate={fetchPractice} />
              </div>
            )}

            {activeTab === 'requests' && (
              <div>
                <div className="mb-4 md:mb-6">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">Join Requests</h3>
                  <p className="text-xs md:text-sm text-gray-600 mt-1">Review and approve therapist join requests</p>
                </div>
                <JoinRequestsList onRequestUpdate={fetchPractice} />
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Regular Member View */}
      {!memberData?.isAdmin && (
        <div className="space-y-6">
          {/* Practice Overview */}
          <Card variant="elevated" padding="large">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-primary/10 rounded-lg flex-shrink-0">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {practiceData.name}
                </h2>
                {practiceData.description && (
                  <p className="text-gray-600 mb-4">{practiceData.description}</p>
                )}
                <div className="flex items-center gap-2">
                  <Badge variant={practiceData.practiceType === 'group' ? 'primary' : 'default'}>
                    {practiceData.practiceType === 'group' ? 'Group Practice' : 'Solo Practice'}
                  </Badge>
                </div>
              </div>
              <Button
                variant="ghost"
                size="small"
                onClick={() => setShowInfoModal(true)}
                title="View practice settings"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </Card>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card variant="default" padding="medium">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Members</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {practiceData.memberCount || 0}
                  </p>
                </div>
              </div>
            </Card>

            <Card variant="default" padding="medium">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Activity className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Patients</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {practiceData.patientCount || 0}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Contact Information */}
          {(practiceData.email || practiceData.phone || practiceData.address) && (
            <Card variant="default" padding="medium">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
              <div className="space-y-3">
                {practiceData.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</p>
                      <p className="text-gray-900 mt-1">{practiceData.email}</p>
                    </div>
                  </div>
                )}
                {practiceData.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</p>
                      <p className="text-gray-900 mt-1">{practiceData.phone}</p>
                    </div>
                  </div>
                )}
                {practiceData.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Address</p>
                      <p className="text-gray-900 mt-1">
                        {practiceData.address.street && `${practiceData.address.street}, `}
                        {practiceData.address.city && `${practiceData.address.city}, `}
                        {practiceData.address.state && `${practiceData.address.state} `}
                        {practiceData.address.zipCode && practiceData.address.zipCode}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Member Info */}
          <Card variant="default" padding="medium">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  Member Account
                </h3>
                <p className="text-sm text-gray-600">
                  You're a member of this practice. Contact your practice admin for management features or to request admin access.
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Modals */}
      {memberData?.isAdmin && (
        <InviteMemberModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onInviteSent={() => {
            fetchPractice();
            setShowInviteModal(false);
          }}
        />
      )}

      {practiceData && (
        <PracticeInfoModal
          isOpen={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          practice={practiceData}
          isAdmin={memberData?.isAdmin}
          onPracticeUpdated={() => {
            fetchPractice();
          }}
        />
      )}
    </div>
  );
};

export default PracticeSettings;
